import React, { useMemo } from 'react'
import { observer } from 'mobx-react-lite'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import Paper from '@mui/material/Paper'
import Snackbar from '@mui/material/Snackbar'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'

import { MerchantLogo, P, Text } from '@mxenabled/mxui'

import { endOfMonth } from 'date-fns/endOfMonth'
import { isBefore } from 'date-fns/isBefore'
import { startOfMonth } from 'date-fns/startOfMonth'

import {
  ANALYTICS_EVENTS,
  Loader,
  useCategoryStore,
  useEvent,
  useGlobalCopyStore,
  useGlobalUiStore,
  useTransactionStore,
  useUserStore,
} from '../../../common'
import { DATE_FORMATS_INTL, formatDate } from '../../../core/constants'
import { Transaction, TransactionType } from '../../../core'
import { fromUnixUTC } from '../../../core/utils/DateUtil'
import { isFeatureEnabled } from '../../../core/utils/UserUtils'
import { ChartTransactionWithDrillDown, EmbeddedCard } from '../../../insights'

import {
  BottomActions,
  DEFAULT_BOTTOM_ACTIONS,
  DEFAULT_TOP_ACTIONS,
  TopActions,
} from '../../constants/Actions'

import {
  AddMerchantBudgetAction,
  CategoryAction,
  DateAction,
  DeleteAction,
  DeleteLogoAction,
  ExcludeAction,
  MemoAction,
  SplitAction,
  TagsAction,
} from './transactiondetails/actions'
import Description from './transactiondetails/Description'
import FlagTransaction from './transactiondetails/FlagTransaction'
import TransactionDetailsView from './transactiondetails/TransactionDetailsView'
import { getEmbeddedInstanceSlotByBeatTemplates } from './getEmbeddedInstanceSlotByBeatTemplates'
import { formatCurrency } from '../../../core/utils/NumberFormatting'
import { buildDynamicCopy } from '../../../core/utils/Localization'

export interface TransactionDetailsProps {
  bottomActions?: BottomActions[]
  canFlagTransaction?: boolean
  topActions?: TopActions[]
  transaction: Transaction
}

const TransactionDetails: React.FC<TransactionDetailsProps> = ({
  bottomActions = DEFAULT_BOTTOM_ACTIONS,
  canFlagTransaction = true,
  topActions = DEFAULT_TOP_ACTIONS,
  transaction,
}) => {
  const { onEvent } = useEvent()
  const { isCopyLoaded } = useGlobalUiStore()
  const { userFeatures } = useUserStore()
  const { accounts: copy, common: commonCopy, insights_feed: insightsCopy } = useGlobalCopyStore()

  const { detailedCategories } = useCategoryStore()
  const {
    alert,
    associatedBeats,
    detailedTransactions,
    cachedStartDate,
    loadEmbeddedInstanceTransactions,
    setAlert,
  } = useTransactionStore()

  const [showTranscationDrilldownDrawer, setShowTransactionDrillDownDrawer] = React.useState(false)

  const [isLoading, setIsLoading] = React.useState(false)
  const [closeTipSection, setCloseTipSection] = React.useState(false)
  const theme = useTheme()

  const showFullNumber = transaction?.number && transaction.number.length > 4

  const isCredit = transaction?.transaction_type === TransactionType.CREDIT

  const beat = React.useMemo(() => {
    return associatedBeats.find((beat) => beat.associated_transaction_guid === transaction.guid)
  }, [associatedBeats, transaction])

  const embeddedData = getEmbeddedInstanceSlotByBeatTemplates(commonCopy, beat)

  const [selectedTabIndex, setSelectedTabIndex] = React.useState(
    embeddedData ? embeddedData.dataSeries.length - 1 : 0,
  )

  React.useEffect(
    () =>
      onEvent(ANALYTICS_EVENTS.TRANSACTION_DETAILS_VIEW, { transaction_guid: transaction.guid }),
    [],
  )

  React.useEffect(() => {
    if (!embeddedData || embeddedData.dataSeries.length === 0) return

    const { date: selectedSeriesItemDate } = embeddedData.dataSeries[selectedTabIndex]

    const start = startOfMonth(selectedSeriesItemDate)

    if (isBefore(start, cachedStartDate)) {
      setIsLoading(true)
      loadEmbeddedInstanceTransactions(start).finally(() => {
        setIsLoading(false)
      })
    }
  }, [selectedTabIndex, embeddedData, cachedStartDate])

  //if merchant_guid is null then no need to show the MerchantBudget action.
  const updatedBottomActions =
    bottomActions.includes(BottomActions.MerchantBudget) && !transaction?.merchant_guid
      ? bottomActions.filter((action) => action !== BottomActions.MerchantBudget)
      : bottomActions

  const embeddedCardDataCategory = useMemo(() => {
    if (embeddedData) {
      const category = detailedCategories.find(
        (cat) => cat.guid === transaction.top_level_category_guid,
      )
      const mean = beat?.payload?.average_amount ?? 0
      const currentAmount = transaction.amount

      let changetype = insightsCopy.general.increased_text
      if (currentAmount - mean < 0) {
        changetype = insightsCopy.general.decreased_text
      }
      const percentageAmount =
        mean === 0 ? 0 : Math.round((Math.abs(currentAmount - mean) / mean) * 100)

      return { category, percentageAmount, changetype }
    }
    return null
  }, [embeddedData, detailedCategories])

  const filteredTransactions = useMemo(() => {
    if (!embeddedData?.dataSeries?.length) {
      return []
    }

    const { guid: selectedSeriesItemGuid, date: selectedSeriesItemDate } =
      embeddedData.dataSeries[selectedTabIndex]

    const start = startOfMonth(selectedSeriesItemDate)
    const end = endOfMonth(selectedSeriesItemDate)

    return detailedTransactions.filter((t) => {
      const transactionDate = fromUnixUTC(t.date)
      return transactionDate >= start && transactionDate <= end && t.guid === selectedSeriesItemGuid
    })
  }, [selectedTabIndex, embeddedData, detailedTransactions, isLoading])

  if (!isCopyLoaded) {
    return <Loader />
  }

  const shouldShowViewOnlyMode = isFeatureEnabled(userFeatures, 'MX_TXN_DETAILS_VIEW_ONLY')

  const bgColor = theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100'

  return (
    <Box className="mx-txn-transaction-details" width="100%">
      {transaction && (
        <Stack>
          <Stack gap={12} p={24}>
            <Paper elevation={2} sx={{ width: 64 }}>
              <MerchantLogo
                categoryGuid={transaction.top_level_category_guid || ''}
                merchantGuid={transaction.merchant_guid || ''}
                size={64}
              />
            </Paper>

            <Description transaction={transaction} viewOnly={shouldShowViewOnlyMode} />

            <Stack direction="row" justifyContent="space-between" sx={{ minHeight: 44 }}>
              <P color={isCredit ? 'success.main' : 'text.primary'} variant="h1">
                {isCredit ? '+' : ''}
                {formatCurrency(transaction.amount, '0,0.00')}
              </P>
              {!shouldShowViewOnlyMode && canFlagTransaction && (
                <FlagTransaction transaction={transaction} />
              )}
            </Stack>

            {embeddedData?.instanceSlot && (
              <EmbeddedCard
                callToAction={embeddedData.callToAction}
                description={
                  buildDynamicCopy(
                    insightsCopy.general.embedded_description,
                    embeddedCardDataCategory!.changetype,
                    <strong>{embeddedCardDataCategory!.percentageAmount}</strong>,
                  ) as string
                }
                instanceSlot={embeddedData.instanceSlot}
                onCtaClick={() => {
                  setShowTransactionDrillDownDrawer(true)
                }}
                title={beat!.html_micro_title!}
              />
            )}
            <Stack sx={{ bgcolor: bgColor, borderRadius: '4px', gap: 4, p: 8 }}>
              <Text bold={true}>
                {`${transaction.account}
                ${
                  showFullNumber
                    ? transaction.number
                    : '\u2022'.repeat(4).concat(transaction.number ?? '')
                }`}
              </Text>
              <Text bold={true} truncate={true} variant="tiny">
                {transaction.feed_description}
              </Text>
            </Stack>
          </Stack>

          <Paper square={true} sx={{ boxShadow: 'none' }}>
            {shouldShowViewOnlyMode ? (
              <TransactionDetailsView transaction={transaction} />
            ) : (
              <List>
                {topActions.map((action, index) => (
                  <React.Fragment key={action}>
                    {action === TopActions.Category && <CategoryAction transaction={transaction} />}
                    {action === TopActions.Date && <DateAction transaction={transaction} />}
                    {action === TopActions.Memo && <MemoAction transaction={transaction} />}
                    {action === TopActions.Tags && <TagsAction transaction={transaction} />}
                    <Divider
                      component="li"
                      variant={index < topActions.length - 1 ? 'inset' : 'fullWidth'}
                    />
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>

          {!shouldShowViewOnlyMode && updatedBottomActions.length > 0 && (
            <React.Fragment>
              <Text bold={true} mb={8} ml={26} mt={16} variant="body1">
                {copy.actions}
              </Text>

              <Paper square={true} sx={{ boxShadow: 'none' }}>
                <List>
                  {updatedBottomActions.map((action, index) => (
                    <React.Fragment key={action}>
                      {action === BottomActions.MerchantBudget && (
                        <AddMerchantBudgetAction transaction={transaction} />
                      )}
                      {action === BottomActions.SplitTransaction && (
                        <SplitAction transaction={transaction} />
                      )}
                      {action === BottomActions.HideTransaction && (
                        <ExcludeAction transaction={transaction} />
                      )}
                      {action === BottomActions.MerchantLogo && (
                        <DeleteLogoAction transaction={transaction} />
                      )}
                      <Divider
                        component="li"
                        variant={index < updatedBottomActions.length - 1 ? 'inset' : 'fullWidth'}
                      />
                    </React.Fragment>
                  ))}

                  {/*
                Show DeleteAction only if:
                - The transaction is manual
                - The transaction is NOT a parent transaction (no parent_guid)
                - The transaction has NOT been split (has_been_split is false)
                
                This ensures delete is hidden for parent or split transactions.
              */}
                  {transaction.is_manual &&
                    !transaction.parent_guid &&
                    !transaction.has_been_split && <DeleteAction transaction={transaction} />}
                </List>
              </Paper>
            </React.Fragment>
          )}
        </Stack>
      )}
      {beat && embeddedData && (
        <ChartTransactionWithDrillDown
          ariaLabel={formatDate(
            embeddedData?.dataSeries[selectedTabIndex]?.date,
            DATE_FORMATS_INTL.MONTH,
          )}
          barChartProps={{
            amounts: {
              average: beat.payload.average_amount,
              formattedAverage: formatCurrency(beat.payload.average_amount, '0,0'),
            },
            color: embeddedCardDataCategory!.category?.color || '',
            data: embeddedData.chartData,
            margin: { bottom: 10, left: 24, right: 24, top: 20 },
            monthlyAmountLabel: insightsCopy.general.monthly_amount,
            onBarClick: (index) => {
              setSelectedTabIndex(index)
            },
            trendAmountLabel: insightsCopy.general.trend,
            legendPosition: 'top',
          }}
          beat={{
            heading: beat.html_title,
            subHeading: {
              category_guid: transaction.category_guid ?? '',
              merchant_guid: transaction.merchant_guid ?? '',
              description: transaction.description ?? '',
            },
          }}
          data={embeddedData.chartData}
          defaultSelectedIndex={selectedTabIndex}
          icon={
            <MerchantLogo
              alt={transaction.description ?? ''}
              categoryGuid={transaction.category_guid ?? ''}
              merchantGuid={transaction.merchant_guid ?? ''}
              size={16}
            />
          }
          isOpen={showTranscationDrilldownDrawer}
          onClose={() => {
            setShowTransactionDrillDownDrawer(false)
            setSelectedTabIndex(embeddedData!.dataSeries.length - 1)
          }}
          onTabChange={(index: number) => {
            setSelectedTabIndex(index)
          }}
          tipSection={
            !closeTipSection
              ? {
                  message: buildDynamicCopy(
                    insightsCopy.general.tip_section_message,
                    <strong>{insightsCopy.general.tip_section_title}</strong>,
                    <strong>{beat.primary_transaction?.description}</strong>,
                  ) as string,
                  onDismiss: () => setCloseTipSection(true),
                }
              : undefined
          }
          title={insightsCopy.general.transaction_history_title}
          transactionListProps={{
            showLoader: isLoading,
            showInsights: true,
            transaction: filteredTransactions,
          }}
        />
      )}
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        autoHideDuration={3500}
        onClose={() => setAlert('')}
        open={Boolean(alert)}
      >
        <Alert closeText="" onClose={() => setAlert('')} severity="success" variant="filled">
          {alert}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default observer(TransactionDetails)
